import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { getFirestore, doc, getDoc, updateDoc } from 'firebase/firestore';
import app from '../../../../backend/firebaseConfig';
import { check2FASessionVerification } from '../../../../services/twoFactorService';

const ProtectedRoute = ({ 
  children, 
  allowedRoles = [], 
  requireAuth = true,
  requireEmailVerification = true,
  redirectTo = '/sign-in' 
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [is2FAVerified, setIs2FAVerified] = useState(false);
  const [pending2FAUid, setPending2FAUid] = useState(null);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const [showCustomAlert, setShowCustomAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertTitle, setAlertTitle] = useState("")
  const [alertImage, setAlertImage] = useState("");
  const [alertButton, setAlertButton] = useState("")
  const [alertButtonType, setAlertButtonType] = useState("false")
  const [alertLink, setAlertLink] = useState("")
  
  // Track loading start time to ensure minimum loading duration
  const loadingStartTimeRef = useRef(Date.now());
  const minimumLoadingTime = 500; // Minimum 500ms loading time to prevent flashing
  const redirectStartTimeRef = useRef(null);
  const minimumRedirectDelay = 2000; // Minimum 2 seconds to show logo during redirect
  const networkDisabledRef = useRef(false);
  const removalHandledRef = useRef(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const auth = getAuth(app);
  const db = getFirestore(app);

  useEffect(() => {
    // Set up cross-tab communication for email verification
    const broadcastChannel = typeof BroadcastChannel !== 'undefined' 
      ? new BroadcastChannel('email_verification') 
      : null;
    
    // Track if we're currently on the sign-in page to prevent unnecessary reloads
    const isOnSignInPage = location.pathname === '/sign-in';
    
    // Throttle reloads to prevent flashing
    let reloadInProgress = false;
    let lastReloadTime = 0;
    const RELOAD_THROTTLE_MS = 2000; // Only allow reloads every 2 seconds
    
    // Listen for email verification events from other tabs
    if (broadcastChannel) {
      const handleEmailVerification = async () => {
        if (auth.currentUser && !isOnSignInPage && !reloadInProgress) {
          const now = Date.now();
          
          // Throttle rapid reload attempts
          if (now - lastReloadTime < RELOAD_THROTTLE_MS) {
            return;
          }
          
          reloadInProgress = true;
          lastReloadTime = now;
          
          // Reload user when email verification happens in another tab
          try {
            await auth.currentUser.reload();
            // The auth state change will trigger automatically, no need to do anything else
          } catch (error) {
            console.error('Error reloading user after cross-tab email verification:', error);
          } finally {
            reloadInProgress = false;
          }
        }
      };
      
      broadcastChannel.addEventListener('message', (event) => {
        // Only handle email_verified events, ignore others
        if (event.data.type === 'email_verified') {
          handleEmailVerification();
        }
      });
    }
    
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // If on sign-in page and not authenticated, don't show loading spinner
      // This prevents flashing when other tabs trigger auth state changes
      if (!user && isOnSignInPage) {
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }
      
      if (user) {
        // Record when loading starts for this auth state change
        loadingStartTimeRef.current = Date.now();
        
        setIsAuthenticated(true);
        setPending2FAUid(sessionStorage.getItem("pending2FA"));
        
        // If on sign-in page, skip full auth processing to prevent flashing
        // Only skip if we're already authenticated (cross-tab noise)
        if (isOnSignInPage && isAuthenticated) {
          // Already authenticated and on sign-in page - cross-tab noise
          setIsLoading(false);
          return;
        }
        
        // Don't reload automatically - it causes flashing
        // Only reload when explicitly needed (like via broadcast channel)
        let refreshedUser = auth.currentUser || user;
        
        // Get user role from Firestore first to check emailVerified status
        try {
          const userRef = doc(db, "users", user.uid);
          let userSnap = await getDoc(userRef);
          
          if (!userSnap.exists()) {
            await new Promise((resolve) => setTimeout(resolve, 500));
            userSnap = await getDoc(userRef);
          }
          
          if (userSnap.exists()) {
            const userData = userSnap.data();
            const role = userData.role || userData.userType;
            setUserRole(role);
            
            // Handle email verification status
            const isMobileOnly = userData.emailVerified === "MobileOnly" || 
                                refreshedUser.email?.includes("@mobileuser.dentalapp.com");
            const signedInViaMobile = sessionStorage.getItem("mobileSignIn") === "true";
            const hasPendingEmail = userData.pendingEmailChange && (!userData.email || userData.email.trim() === "");
            
            // Check Firebase Auth emailVerified first (source of truth) - use refreshed user
            const firebaseEmailVerified = refreshedUser.emailVerified;
            
            if (isMobileOnly || signedInViaMobile || hasPendingEmail) {
              // Mobile-only users OR users who signed in via mobile OR users with pending email skip email verification
              setIsEmailVerified(true);
            } else if (firebaseEmailVerified) {
              // Firebase Auth says email is verified - this is the source of truth
              // Update Firestore if needed to sync
              if (userData.emailVerified !== "Yes") {
                await updateDoc(userRef, { emailVerified: "Yes" });
              }
              setIsEmailVerified(true);
            } else if (userData.emailVerified === "Yes") {
              // Firestore says verified but Firebase Auth doesn't (edge case)
              setIsEmailVerified(true);
            } else {
              setIsEmailVerified(false);
            }
            
            // Check 2FA verification status
            const twoFactorEnabled = userData.twoFactorEnabled === true;
            if (twoFactorEnabled) {
              // Check if 2FA was just verified - SignIn already waited, so trust it
              const justVerified = sessionStorage.getItem("2FAJustVerified") === "true";
              if (justVerified) {
                // Check the status (should be ready now since SignIn waited)
                const twoFAStatus = await check2FASessionVerification(user.uid);
                
                if (twoFAStatus.verified) {
                  setIs2FAVerified(true);
                  
                  // Grant access
                  if (allowedRoles.length === 0 || allowedRoles.includes(role)) {
                    setHasAccess(true);
                  } else {
                    setHasAccess(false);
                  }
                } else {
                  // Still not verified - wait a bit more as backup
                  await new Promise(resolve => setTimeout(resolve, 500));
                  
                  const retryStatus = await check2FASessionVerification(user.uid);
                  if (retryStatus.verified) {
                    setIs2FAVerified(true);
                    if (allowedRoles.length === 0 || allowedRoles.includes(role)) {
                      setHasAccess(true);
                    } else {
                      setHasAccess(false);
                    }
                  } else {
                    setHasAccess(false);
                    setIs2FAVerified(false);
                    setIsLoading(false);
                    return;
                  }
                }
              } else {
                // Normal 2FA check when flag is not set
                const twoFAStatus = await check2FASessionVerification(user.uid);
                setIs2FAVerified(twoFAStatus.verified);
                
                if (!twoFAStatus.verified) {
                  setHasAccess(false);
                  setIsLoading(false);
                  return;
                }
              }
            } else {
              setIs2FAVerified(true); // No 2FA required
            }
            
            // Check if user has required role
            if (allowedRoles.length === 0 || allowedRoles.includes(role)) {
              setHasAccess(true);
            } else {
              setHasAccess(false);
            }
          } else {
            setHasAccess(false);
            setUserRole(null);
            setIsEmailVerified(false);
            setIs2FAVerified(false);
            
            if (!removalHandledRef.current) {
              removalHandledRef.current = true;
              
              try {
                if (auth.currentUser) {
                  await signOut(auth);
                }
              } catch (signOutError) {
                if (signOutError?.code !== 'auth/no-current-user') {
                  console.error('Error signing out removed user:', signOutError);
                }
              }
              
              navigate('/sign-in', {
                state: {
                  from: location.pathname,
                  message: 'Your session has ended. Please sign in again.'
                },
                replace: true
              });
            }
            setIsLoading(false);
            return;
          }
        } catch (error) {
          if (error?.code === "permission-denied") {
            setHasAccess(false);
            setUserRole(null);
            setIsEmailVerified(false);
            setIs2FAVerified(false);
            if (!removalHandledRef.current) {
              removalHandledRef.current = true;
              try {
                if (auth.currentUser) {
                  await signOut(auth);
                }
              } catch (signOutError) {
                if (signOutError?.code !== "auth/no-current-user") {
                  console.error('Error signing out removed user:', signOutError);
                }
              }
              navigate('/sign-in', {
                state: {
                  from: location.pathname,
                  message: 'Your session has ended. Please sign in again.'
                },
                replace: true
              });
            }
            setIsLoading(false);
            return;
          } else {
            console.error('Error fetching user role:', error);
            setHasAccess(false);
          }
        }
      } else {
        setIsAuthenticated(false);
        setUserRole(null);
        setIsEmailVerified(false);
        setHasAccess(false);
        setIs2FAVerified(false);
        // Clear mobile sign-in flag on sign-out
        sessionStorage.removeItem("mobileSignIn");
        removalHandledRef.current = false;
        
        // No user - set loading to false immediately
        setIsLoading(false);
        return;
      }
      
      // Ensure minimum loading time has passed before setting loading to false
      const elapsed = Date.now() - loadingStartTimeRef.current;
      const remainingTime = Math.max(0, minimumLoadingTime - elapsed);
      
      setTimeout(() => {
        setIsLoading(false);
      }, remainingTime);
    });

    return () => {
      unsubscribe();
      if (broadcastChannel) {
        broadcastChannel.close();
      }
      // Reset redirecting state on cleanup
      setIsRedirecting(false);
      redirectStartTimeRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, db, allowedRoles]);

  useEffect(() => {
    // During controlled sign-in flows we may set a suppression flag
    // to avoid any route redirects (e.g., verify-email) while we probe
    // credentials or immediately sign the user back out. Honor that
    // on the sign-in page to prevent flicker/bounce.
    const suppressRedirects = sessionStorage.getItem('suppressPostSignInRedirect') === 'true';
    if (suppressRedirects && location.pathname === '/sign-in') {
      return;
    }

    if (!isLoading) {
      if (!requireAuth) {
        // Public route - no authentication required
        setHasAccess(true);
      } else if (!isAuthenticated) {
        // Authentication required but user not signed in
        navigate(redirectTo, { 
          state: { from: location.pathname, search: location.search },
          replace: true 
        });
      } else if (requireEmailVerification && !isEmailVerified) {
        // Final check Firebase Auth emailVerified status before redirecting
        // This prevents race conditions where state hasn't updated yet
        const currentUser = auth.currentUser;
        if (!currentUser) {
          // User has been signed out (possibly removed); stop further checks
          setIsEmailVerified(false);
          return;
        }

        if (currentUser.emailVerified) {
          // Email is verified in Firebase Auth - update state and don't redirect
          setIsEmailVerified(true);
          return;
        }
        
        // CRITICAL: Check if user is mobile-only or has pending email before redirecting
        // This prevents flashing redirect to verify-email for mobile-only users
        const isMobileOnlyEmail = currentUser?.email?.includes("@mobileuser.dentalapp.com");
        const mobileSignInFlag = sessionStorage.getItem("mobileSignIn") === "true";
        
        // Quick check for mobile-only before async Firestore call
        if (isMobileOnlyEmail || mobileSignInFlag) {
          setIsEmailVerified(true);
          return;
        }
        
        // Check Firestore asynchronously for pending email
        const checkPendingEmail = async () => {
          if (!currentUser) {
            return true;
          }
          try {
            const userRef = doc(db, "users", currentUser.uid);
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
              const userData = userSnap.data();
              const hasPendingEmail = userData.pendingEmailChange && (!userData.email || userData.email.trim() === "");
              const isMobileOnly = userData.emailVerified === "MobileOnly";
              
              if (hasPendingEmail || isMobileOnly) {
                // User has pending email or is mobile-only - don't redirect to verify-email
                setIsEmailVerified(true);
                return true; // Skip redirect
              }
            }
            return false; // Should redirect
          } catch (error) {
            console.error("Error checking user data for email verification:", error);
            return false; // On error, proceed with redirect
          }
        };
        
        // Run the check and handle redirect
        checkPendingEmail().then(async (shouldSkip) => {
          if (shouldSkip) return;
          
          // For dashboard (or any protected path) while unverified, force sign-out and send to sign-in
          // to avoid showing the dashboard, even briefly.
          if (!isRedirecting) {
            setIsRedirecting(true);
            try {
              await signOut(auth);
            } catch (err) {
              console.error("Error signing out unverified user:", err);
            }
            navigate("/sign-in", { replace: true });
          }
        });
        
        return; // Exit early to prevent rendering
      } else if (!is2FAVerified) {
        // If there's a pending 2FA session for the same user on sign-in page, allow it to handle 2FA
        // Otherwise, redirect to sign-in to complete 2FA verification
        const currentUid = auth.currentUser?.uid;
        const pending2FAValue = pending2FAUid || sessionStorage.getItem("pending2FA");
        const isOnSignInPage = location.pathname === '/sign-in';
        const justVerified = sessionStorage.getItem("2FAJustVerified") === "true";
        
        // If 2FA was just verified, don't redirect - just wait for the check to complete
        if (justVerified) {
          setHasAccess(false);
          // Don't set loading to false - keep showing loading state
          return;
        }
        
        // Check if pending2FA is set (either "pending" placeholder or actual UID)
        // This prevents redirect when handleSignIn is in the process of showing the modal
        if (pending2FAValue && (pending2FAValue === currentUid || pending2FAValue === "pending")) {
          if (isOnSignInPage) {
            // User is on sign-in page with pending 2FA - allow sign-in page to handle it
            // Don't redirect, but also don't grant access
            setHasAccess(false);
            return;
          }
          // If not on sign-in page but pending2FA is set, wait for sign-in page to handle it
          setHasAccess(false);
          return;
        } else {
          // User authenticated but 2FA not verified -> redirect to sign-in to start 2FA
          if (!isRedirecting) {
            setIsRedirecting(true);
            navigate('/sign-in', { 
              state: { 
                from: location.pathname,
                require2FA: true,
                message: "Two-factor authentication verification required"
              },
              replace: true 
            });
          }
        }
      } else if (!hasAccess) {
        // User authenticated but doesn't have required role
        navigate('/unauthorized', { 
          state: { 
            from: location.pathname,
            userRole: userRole,
            requiredRoles: allowedRoles
          },
          replace: true 
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isAuthenticated, isEmailVerified, is2FAVerified, hasAccess, requireAuth, requireEmailVerification, navigate, redirectTo, location, userRole, allowedRoles, auth]);
  
  // If authentication not required or user has access, render children
  if (!requireAuth || hasAccess) {
    return children;
  }

  // This should not render, but just in case
  return null;
};

export default ProtectedRoute;
